// @flow
import * as React from 'react';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';

import assertions from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import { ensureLeading1 } from 'lib/phoneNumbers';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import { FamilyTypeahead } from 'users/components/FamilyTypeahead';
import ToastActions from 'actions/ToastActions';

function formatUserAddress(value: string) {
  return Number(value) === parseInt(value, 10) ? `sms://+${ensureLeading1(value)}` : value;
}

export type DirectAddFamilyOptions = {
  orgUuid: string,
};

type Props = {
  directAddOptions?: DirectAddFamilyOptions,
  relationToAdd: 'parent' | 'child',
  onRequestHide: () => {},
  onAddFamilyMember?: () => void,
  selfId: number,
  selfUuid?: string,
  eventPrefix: ?string,

  // gql mutations
  addExistingParent: ({
    variables: {
      input: {
        parent_id: ?number,
        child_id: ?number,
      },
    },
  }) => Promise<*>,
  addNewParent: ({
    variables: {
      input: {
        organizationUuid: string,
        childUuid: string,
        name: string,
        address: string,
      },
    },
  }) => Promise<*>,
};

type State = {
  showDirectAddInput: boolean,
  directAddName: ?string,
  directAddAddress: ?string,
  selectedFamilyMember: ?*,
};

class AddFamilyModal extends React.Component<Props, State> {
  state = {
    showDirectAddInput: false,
    directAddName: null,
    directAddAddress: null,
    selectedFamilyMember: null,
  };

  static defaultProps = {
    onRequestHide: () => {},
  };

  trackEvent = (eventKey: string) => {
    const { eventPrefix } = this.props;
    if (!eventPrefix) {
      return;
    }
    EventTracking.trackKey(`${eventPrefix}.${eventKey}`);
  };

  trackConnectButtonClick = () => {
    this.trackEvent('add_family_dialog.connect.click');
  };

  handleDirectAddClick = (query: string) => {
    this.setState({ showDirectAddInput: true, directAddName: query });
    this.trackEvent('add_family_dialog.add_parent.select');
  };

  handleFamilyMemberSelection = (familyMemberUser: *) => {
    this.setState({ selectedFamilyMember: familyMemberUser });
    this.trackEvent('add_family_dialog.existing_parent.select');
  };

  addParentOrChild = (familyMember: ?*) => {
    this.trackConnectButtonClick();
    if (!familyMember) {
      ToastActions.showErrorToast(
        'Oops! Looks like something went wrong - please close the dialog and try again.'
      );
      console.error(
        `AddFamilyModal - Tried to add a ${this.props.relationToAdd} without anything selected`
      );
      return;
    }

    const [parentId, childId] =
      this.props.relationToAdd === 'child'
        ? [this.props.selfId, familyMember.id]
        : [familyMember.id, this.props.selfId];

    this.props
      .addExistingParent({ variables: { input: { parent_id: parentId, child_id: childId } } })
      .then(
        () => {
          if (this.props.onAddFamilyMember) {
            this.props.onAddFamilyMember();
          }
          ToastActions.showSuccessToast(`A ${this.props.relationToAdd} has been added.`);
          this.props.onRequestHide();
        },
        e => {
          ToastActions.showAPIError(e);
          console.error(`Adding ${this.props.relationToAdd} relation failed`);
          console.warn(e);
        }
      )
      .catch(errorLogger(`Adding ${this.props.relationToAdd} relation failed on runtime`));
  };

  directAddParent = (parentName: ?string, parentAddress: ?string) => {
    this.trackConnectButtonClick();
    const missingFieldError = (missingField: string) => {
      console.error(`AddFamilyModal - Tried to direct add a parent without a ${missingField}`);
      ToastActions.showErrorToast(
        'Oops! Looks like something went wrong - please close the dialog and try again.'
      );
    };

    if (!parentName) {
      missingFieldError('name');
      return;
    }
    if (!parentAddress) {
      missingFieldError('uid');
      return;
    }
    if (!this.props.directAddOptions) {
      missingFieldError('direct add options prop');
      return;
    }
    if (!this.props.selfUuid) {
      missingFieldError('user uuid');
      return;
    }

    this.props
      .addNewParent({
        variables: {
          input: {
            organizationUuid: this.props.directAddOptions.orgUuid,
            childUuid: this.props.selfUuid,
            name: parentName,
            address: parentAddress,
          },
        },
      })
      .then(
        () => {
          if (this.props.onAddFamilyMember) {
            this.props.onAddFamilyMember();
          }
          this.props.onRequestHide();
          ToastActions.showSuccessToast(
            // $FlowIssue
            `${parentName} has been added to Remind.`
          );
        },
        e => {
          ToastActions.showAPIError(e);
          console.error(`Direct adding parent relation failed`);
          console.warn(e);
        }
      )
      .catch(errorLogger(`Direct adding parent relation failed on runtime`));
  };

  renderDirectAdd = () => {
    return (
      <React.Fragment>
        <FormElement
          label="Full name"
          validate={e => {
            assertions.notEmpty(e.target.value);
            assertions.numberOfWordsGreaterOrEqualTo(
              e.target.value,
              2,
              'Please enter a first and last name.'
            );
          }}
          onValidChange={e => this.setState({ directAddName: e.target.value })}
          onInvalidChange={() => this.setState({ directAddName: undefined })}
          element={Input}
          elementProps={{
            defaultValue: this.state.directAddName,
            placeholder: 'Enter a first and last name',
          }}
        />
        <FormElement
          label="Email address or phone number"
          validate={e => {
            assertions.notEmpty(e.target.value);
            assertions.emailOrPlausibleNorthAmericanPhoneNumber(e.target.value);
          }}
          onValidChange={e => {
            const directAddAddress = formatUserAddress(e.target.value);
            this.setState({ directAddAddress });
          }}
          onInvalidChange={() => this.setState({ directAddAddress: undefined })}
          element={Input}
        />
      </React.Fragment>
    );
  };

  render() {
    const { relationToAdd, onRequestHide, directAddOptions } = this.props;
    const {
      showDirectAddInput,
      directAddName,
      directAddAddress,
      selectedFamilyMember,
    } = this.state;

    return (
      <Dialog
        size="small"
        title={relationToAdd === 'parent' ? 'Connect a parent or guardian' : 'Connect a child'}
        onDialogDismiss={() => {
          this.trackEvent('add_family_dialog.dismiss');
          onRequestHide();
        }}
        className="AdminAddAdminModal"
        data-test="add-family-modal-dialog">
        <BoxBody divided>
          {showDirectAddInput ? (
            this.renderDirectAdd()
          ) : (
            <FamilyTypeahead
              relation={relationToAdd}
              allowDirectAdd={!!directAddOptions && relationToAdd === 'parent'}
              onSelectFamilyMember={this.handleFamilyMemberSelection}
              onDirectAddClick={this.handleDirectAddClick}
            />
          )}
          <Button
            type="submit"
            disabled={!(directAddName && directAddAddress) && !selectedFamilyMember}
            onClick={
              directAddName && directAddAddress
                ? () => this.directAddParent(directAddName, directAddAddress)
                : () => this.addParentOrChild(selectedFamilyMember)
            }>
            Add {relationToAdd}
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}

const withAddExistingParentMutation = graphql(
  gql`
    mutation addExistingParent($input: AddParentRelationshipInput!) {
      addParentRelationship(input: $input) {
        error {
          message
        }
      }
    }
  `,
  {
    name: 'addExistingParent',
  }
);

const withAddNewParentMutation = graphql(
  gql`
    mutation addNewParent($input: AddUserParentInput!) {
      addUserParent(input: $input) {
        error {
          message
        }
      }
    }
  `,
  {
    name: 'addNewParent',
  }
);

export { AddFamilyModal };

export default compose(
  withAddExistingParentMutation,
  withAddNewParentMutation
)(AddFamilyModal);
